import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useStudentData } from '@/hooks/useStudentData';
import { formatDate, getWeekday } from '@/lib/date';
import { getStatusMeta, lessonStatusMap } from '@/lib/statusMap';
import StatusBadge from '@/components/base/StatusBadge';

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function StudentHome() {
  const { profile } = useAuth();
  const {
    student,
    group,
    lessons,
    records,
    assignments,
    submissions,
    announcements,
    whatsappLink,
    loading,
    error,
    refetch,
  } = useStudentData();

  const firstName = (profile?.full_name ?? 'Öğrenci').split(' ')[0];
  const todayYmd = toYmd(new Date());

  const upcoming = useMemo(() => {
    return lessons
      .filter((l) => l.date && l.date >= todayYmd)
      .slice()
      .sort((a, b) => `${a.date}${a.start_time ?? ''}`.localeCompare(`${b.date}${b.start_time ?? ''}`));
  }, [lessons, todayYmd]);

  const nextLesson = upcoming[0] ?? null;
  const nextUpcoming = upcoming.slice(1, 5);

  const latestRecord = records[0] ?? null;
  const pendingAssignments = assignments.filter(
    (a) => !submissions.some((s) => s.assignment_id === a.id),
  );
  const latestAnnouncement = announcements[0] ?? null;

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Ana sayfa yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Veriler yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button
            type="button"
            onClick={refetch}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Merhaba {firstName} 👋</h1>
        <p className="mt-1 text-sm text-foreground-600">Derslerine buradan kolayca erişebilirsin.</p>
      </div>

      {/* Sıradaki ders — canlı link vurgusu */}
      <section className="overflow-hidden rounded-2xl border border-background-200 bg-gradient-to-br from-primary-50 via-background-50 to-accent-50">
        <div className="flex flex-col gap-5 p-6 md:p-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-1">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              <i className="ri-flashlight-line" /> Sıradaki Ders
            </span>

            {nextLesson ? (
              <>
                <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {nextLesson.title}
                </h2>
                <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-600">
                  <span className="inline-flex items-center gap-1.5 capitalize">
                    <i className="ri-calendar-line text-foreground-500" />
                    {nextLesson.date ? `${formatDate(nextLesson.date)} · ${getWeekday(nextLesson.date)}` : '—'}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-time-line text-foreground-500" />
                    {nextLesson.start_time ?? '--:--'} - {nextLesson.end_time ?? '--:--'}
                  </span>
                  {group && (
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-team-line text-foreground-500" />
                      {group.name}
                    </span>
                  )}
                </p>
                {nextLesson.subject && (
                  <p className="mt-2 text-sm text-foreground-700">Konu: {nextLesson.subject}</p>
                )}
              </>
            ) : (
              <>
                <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  Henüz planlanmış dersin yok
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  Öğretmenin bir ders planladığında burada görünecek.
                </p>
              </>
            )}
          </div>

          {nextLesson?.live_link ? (
            <a
              href={nextLesson.live_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-primary-600 lg:self-center"
            >
              <i className="ri-vidicon-line text-base" />
              Canlı Derse Katıl
            </a>
          ) : (
            nextLesson && (
              <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-background-200/70 px-5 py-3 text-sm font-semibold text-foreground-600 lg:self-center">
                <i className="ri-time-line" /> Canlı link henüz eklenmedi
              </span>
            )
          )}
        </div>
      </section>

      {/* Hızlı erişim kartları */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Link
          to="/ogrenci/kayitlar"
          className="group rounded-xl border border-background-200 bg-white p-5 transition hover:border-background-300"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-red-50 text-red-500">
              <i className="ri-vidicon-line text-xl" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground-500">Son Ders Kaydı</p>
              <p className="truncate text-sm font-semibold text-foreground-950">
                {latestRecord ? latestRecord.title : 'Kayıt yok'}
              </p>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400 transition group-hover:translate-x-0.5 group-hover:text-primary-600" />
          </div>
        </Link>

        <Link
          to="/ogrenci/odevler"
          className="group rounded-xl border border-background-200 bg-white p-5 transition hover:border-background-300"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-50 text-amber-500">
              <i className="ri-task-line text-xl" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground-500">Bekleyen Ödev</p>
              <p className="text-sm font-semibold text-foreground-950">
                {pendingAssignments.length > 0
                  ? `${pendingAssignments.length} ödev teslim bekliyor`
                  : 'Bekleyen ödev yok'}
              </p>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400 transition group-hover:translate-x-0.5 group-hover:text-primary-600" />
          </div>
        </Link>

        <Link
          to="/ogrenci/duyurular"
          className="group rounded-xl border border-background-200 bg-white p-5 transition hover:border-background-300"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-50 text-secondary-500">
              <i className="ri-megaphone-line text-xl" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground-500">Son Duyuru</p>
              <p className="truncate text-sm font-semibold text-foreground-950">
                {latestAnnouncement ? latestAnnouncement.title : 'Duyuru yok'}
              </p>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400 transition group-hover:translate-x-0.5 group-hover:text-primary-600" />
          </div>
        </Link>

        <Link
          to="/ogrenci/whatsapp"
          className="group rounded-xl border border-background-200 bg-white p-5 transition hover:border-background-300"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500">
              <i className="ri-whatsapp-line text-xl" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground-500">WhatsApp Grubu</p>
              <p className="truncate text-sm font-semibold text-foreground-950">
                {whatsappLink ? 'Gruba katıl' : 'Link eklenmemiş'}
              </p>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400 transition group-hover:translate-x-0.5 group-hover:text-primary-600" />
          </div>
        </Link>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Yaklaşan dersler */}
        <section className="xl:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                <i className="ri-calendar-2-line text-base" />
              </span>
              <h3 className="font-heading text-lg font-bold text-foreground-950">Yaklaşan Dersler</h3>
            </div>
            <Link
              to="/ogrenci/program"
              className="text-sm font-semibold text-primary-600 transition hover:text-primary-700 whitespace-nowrap"
            >
              Tümünü Gör
            </Link>
          </div>

          {nextUpcoming.length === 0 ? (
            <div className="rounded-xl border border-dashed border-background-300 bg-white p-10 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-calendar-2-line text-2xl" />
              </div>
              <p className="mt-4 text-sm font-semibold text-foreground-800">Yaklaşan ders yok</p>
              <p className="mt-1 text-sm text-foreground-500">Yeni dersler planlandığında burada görünecek.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {nextUpcoming.map((lesson) => {
                const status = getStatusMeta(lesson.status, lessonStatusMap);
                return (
                  <div
                    key={lesson.id}
                    className="flex flex-col gap-3 rounded-xl border border-background-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-center gap-3.5">
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white"
                        style={{ backgroundColor: group?.color ?? '#94a3b8' }}
                      >
                        <i className={`${lesson.type === 'birebir' ? 'ri-user-line' : 'ri-team-line'} text-lg`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-heading text-sm font-bold text-foreground-950">{lesson.title}</p>
                        <p className="mt-0.5 truncate text-xs capitalize text-foreground-500">
                          {lesson.date ? `${formatDate(lesson.date)} · ${getWeekday(lesson.date)}` : '—'} ·{' '}
                          {lesson.start_time ?? '--:--'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge meta={status} />
                      {lesson.live_link && (
                        <a
                          href={lesson.live_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-200"
                        >
                          <i className="ri-vidicon-line" /> Katıl
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Grubum / Bilgilerim */}
        <section>
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
              <i className="ri-team-line text-base" />
            </span>
            <h3 className="font-heading text-lg font-bold text-foreground-950">Grubum</h3>
          </div>

          <div className="rounded-xl border border-background-200 bg-white p-5">
            {group ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl text-white"
                    style={{ backgroundColor: group.color ?? '#94a3b8' }}
                  >
                    <i className="ri-team-line text-xl" />
                  </div>
                  <div>
                    <p className="font-heading text-base font-bold text-foreground-950">{group.name}</p>
                    <p className="text-xs text-foreground-500">{group.level ?? 'Seviye belirtilmedi'}</p>
                  </div>
                </div>

                {(group.day || group.time) && (
                  <div className="rounded-lg bg-background-50 p-3.5">
                    <p className="flex items-center gap-2 text-xs font-semibold text-foreground-500">
                      <i className="ri-time-line" /> Ders Günü / Saati
                    </p>
                    <p className="mt-1 text-sm font-medium capitalize text-foreground-900">
                      {group.day ?? '—'} · {group.time ?? '—'}
                    </p>
                  </div>
                )}

                {student?.level && (
                  <div className="rounded-lg bg-background-50 p-3.5">
                    <p className="flex items-center gap-2 text-xs font-semibold text-foreground-500">
                      <i className="ri-bar-chart-2-line" /> Seviyem
                    </p>
                    <p className="mt-1 text-sm font-medium text-foreground-900">{student.level}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-4 text-center">
                <p className="text-sm text-foreground-600">Henüz size atanmış bir grup bulunmuyor.</p>
                <p className="mt-1 text-xs text-foreground-500">Ders içerikleriniz tanımlandığında burada görünecek.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}